import {MessageDescriptor, useIntl} from 'react-intl'
import styled from '@emotion/styled'

import {Alert} from '@vio/atlas/Alert'
import {Button} from '@vio/atlas/Button'
import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/Icon'
import {Tag} from '@vio/atlas/Tag'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {SCORES} from '../constants'

import type {SurveyBannerStatus} from '../constants'

export interface SurveyBannerContentProps {
  /** Statement shoppers rate, shown in quotes. */
  statement: MessageDescriptor
  /** Score awaiting confirmation; drives the loading indicator while submitting. */
  selectedScore?: number
  /** idle: awaiting an answer. submitting: sending the score. submitted: answer recorded. */
  status?: SurveyBannerStatus
  /** Called with the score (1-7) when a button is pressed. */
  onSelect: (score: number) => void
  /** Called when the close control is pressed. */
  onClose: () => void
}

interface AnswerButtonProps {
  value: number
  isSelected: boolean
  isLoading: boolean
  isDisabled: boolean
  onSelect: (score: number) => void
}

const Wrapper = styled.div`
  padding: ${cssTheme.layout.spacing.s400};
  background-color: ${cssTheme.colors.background.neutral.primary};
  border: solid ${cssTheme.layout.borderWidth.bw200};
  border-color: ${cssTheme.colors.border.brand.tertiary};
  border-radius: ${cssTheme.layout.radius.md};
  ${linariaMq.desktopSm} {
    padding: ${cssTheme.layout.spacing.s500};
    border: solid ${cssTheme.layout.borderWidth.bw100};
    border-radius: ${cssTheme.layout.radius.lg};
    border-color: ${cssTheme.colors.border.neutral.secondary};
  }
`

const AnswerButton = ({
  value,
  isSelected,
  isLoading,
  isDisabled,
  onSelect
}: AnswerButtonProps) => (
  <Button
    loading={isLoading}
    disabled={isDisabled}
    onClick={() => onSelect(value)}
    variant={isSelected ? 'primary' : 'tertiary'}
    fullWidth
  >
    <Text variant="labelS">{value}</Text>
  </Button>
)

export const SurveyBannerContent = ({
  statement,
  selectedScore,
  status = 'idle',
  onSelect,
  onClose
}: SurveyBannerContentProps) => {
  const {isMobile} = useDeviceLayout()
  const {formatMessage} = useIntl()
  const isSubmitting = status === 'submitting'

  if (status === 'submitted') {
    return (
      <Alert
        variant="success"
        icon={<Icon name="Checkmark" size="sm" />}
        onClose={onClose}
      >
        <Text variant="labelM">
          <FormattedMessageWrapper
            id="trustSurveyBanner.thanks"
            defaultMessage="Thanks, that helps."
          />
        </Text>
        <Text variant="bodyS">
          <FormattedMessageWrapper
            id="trustSurveyBanner.recorded"
            defaultMessage="Your answer is recorded."
          />
        </Text>
      </Alert>
    )
  }

  return (
    <Wrapper>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        marginBottom="s400"
      >
        <Tag iconStart={<Icon name="VioIcon" size="sm" />}>
          <FormattedMessageWrapper
            id="trustSurveyBanner.quickQuestion"
            defaultMessage="Quick question"
          />
        </Tag>
        <Button
          variant="ghost"
          size="sm"
          hasTouchArea
          onClick={onClose}
          iconStart={
            <Icon
              name="Close"
              size="sm"
              colorPath="content.neutral.secondary"
            />
          }
        />
      </Box>
      <Box marginBottom={isMobile ? 's200' : 's000'}>
        <Text
          variant={isMobile ? 'bodyXS' : 'bodyS'}
          colorPath="content.neutral.secondary"
        >
          <FormattedMessageWrapper
            id="trustSurveyBanner.doYouAgree"
            defaultMessage="How much do you agree?"
          />
        </Text>
      </Box>
      <Text
        className="chromatic-ignore"
        variant={isMobile ? 'labelS' : 'labelM'}
        colorPath="content.neutral.primary"
      >
        {formatMessage(statement)}
      </Text>

      <Box
        role="group"
        display="grid"
        gridTemplateColumns="repeat(7, minmax(0, 1fr))"
        gap="s300"
        marginTop="s400"
        marginBottom="s250"
      >
        {SCORES.map(score => (
          <AnswerButton
            key={score}
            value={score}
            isSelected={selectedScore === score}
            isLoading={isSubmitting && selectedScore === score}
            isDisabled={isSubmitting && selectedScore !== score}
            onSelect={onSelect}
          />
        ))}
      </Box>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Text variant="bodyXS" colorPath="content.danger.secondary">
          <FormattedMessageWrapper
            id="trustSurveyBanner.disagree"
            defaultMessage="Strongly disagree"
          />
        </Text>
        <Text variant="bodyXS" colorPath="content.success.secondary">
          <FormattedMessageWrapper
            id="trustSurveyBanner.agree"
            defaultMessage="Strongly agree"
          />
        </Text>
      </Box>
    </Wrapper>
  )
}
